import React from "react";
import {
  Dialog,
  DialogActions,
  Button,
  Typography,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DialogContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    padding: theme.spacing(2),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: "#f8f9fa",
  borderRadius: 8,
  boxShadow: "none",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #2196F3 0%, #1976D2 100%)",
    borderRadius: "8px 8px 0 0",
  },
}));

const ValidationFormControl = styled(FormControl)(({ theme, isvalid }) => ({
  "& .MuiInputLabel-root": {
    color: isvalid === "false" ? theme.palette.warning.main : undefined,
    fontWeight: 500,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-2px)",
    },
    "& fieldset": {
      borderColor: isvalid === "false" ? theme.palette.warning.main : undefined,
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: isvalid === "false" ? theme.palette.warning.main : undefined,
    },
  },
  marginBottom: "20px",
}));

const StyledMatchTypeChip = styled(Chip)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  height: "24px",
  borderRadius: "12px",
  fontWeight: 600,
  fontSize: "0.75rem",
  "& .MuiChip-label": {
    padding: "0 12px",
  },
  "&.exact": {
    backgroundColor: "rgba(76, 175, 80, 0.1)",
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,
  },
  "&.variant": {
    backgroundColor: "rgba(33, 150, 243, 0.1)",
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
  "&.similarity": {
    backgroundColor: "rgba(255, 152, 0, 0.1)",
    color: theme.palette.warning.main,
    borderColor: theme.palette.warning.main,
  },
  "&.custom": {
    backgroundColor: "rgba(156, 39, 176, 0.1)",
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  "& .MuiTypography-h5": {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  "& .MuiTypography-body1": {
    color: theme.palette.text.secondary,
  },
}));

const MappingSuggestionModal = ({
  open,
  onClose,
  suggestions,
  unmappedColumns,
  warnings,
  onApply,
  onUpdateMapping,
  fieldDescriptions,
}) => {
  const allFieldsMapped = Object.values(suggestions).every(
    (value) => value.header
  );
  const isValidMapping = (value) => {
    if (!value.header) return false;
    const availableColumns = [
      ...unmappedColumns,
      ...Object.values(suggestions)
        .map((s) => s.header)
        .filter(Boolean),
    ];
    return availableColumns.includes(value.header);
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <HeaderSection>
        <Typography variant="h5">Column Mapping Configuration</Typography>
        <Typography variant="body1">
          Match your CSV columns to our required fields. We've made some
          suggestions based on your data.
        </Typography>
      </HeaderSection>

      {warnings.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {warnings.map((warning, index) => (
            <Alert
              key={index}
              severity="warning"
              sx={{
                mb: 1,
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  fontSize: "1.5rem",
                },
              }}
            >
              {warning}
            </Alert>
          ))}
        </Box>
      )}

      <DialogContent sx={{ px: 0 }}>
        <Grid container spacing={3}>
          {unmappedColumns.length > 0 && (
            <Grid xs={12}>
              <StyledPaper>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Available Unmapped Columns
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  {unmappedColumns.map((column) => (
                    <Chip
                      key={column}
                      label={column}
                      variant="outlined"
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "rgba(0, 0, 0, 0.02)",
                      }}
                    />
                  ))}
                </Box>
              </StyledPaper>
            </Grid>
          )}
          <Grid xs={12}>
            <StyledPaper>
              {Object.entries(suggestions).map(([field, value]) => (
                <ValidationFormControl
                  key={field}
                  fullWidth
                  isvalid={isValidMapping(value).toString()}
                >
                  <InputLabel id={`mapping-${field}-label`}>
                    {field} {!value.header && "(Required)"}
                  </InputLabel>
                  <Select
                    labelId={`mapping-${field}-label`}
                    value={value.header || ""}
                    onChange={(e) => onUpdateMapping(field, e.target.value)}
                    label={`${field} ${!value.header ? "(Required)" : ""}`}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {[...unmappedColumns, value.header]
                      .filter(Boolean)
                      .sort()
                      .map((column) => (
                        <MenuItem key={column} value={column}>
                          {column}
                        </MenuItem>
                      ))}
                  </Select>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mt: 1,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color={
                        !isValidMapping(value)
                          ? "warning.main"
                          : "text.secondary"
                      }
                      sx={{ fontStyle: "italic" }}
                    >
                      {fieldDescriptions[field]}
                    </Typography>
                    {value.header && (
                      <StyledMatchTypeChip
                        label={
                          value.matchType ||
                          (value.isOriginalSuggestion
                            ? value.confidence >= 0.9
                              ? "Exact Match"
                              : value.confidence >= 0.7
                              ? "Variant Match"
                              : "Similar Match"
                            : "Custom Match")
                        }
                        className={
                          value.matchType ||
                          (value.isOriginalSuggestion
                            ? value.confidence >= 0.9
                              ? "exact"
                              : value.confidence >= 0.7
                              ? "variant"
                              : "similarity"
                            : "custom")
                        }
                        variant="outlined"
                      />
                    )}
                  </Box>
                </ValidationFormControl>
              ))}
            </StyledPaper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ mt: 3, px: 0, pb: 0 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{
            borderRadius: 2,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onApply}
          color="primary"
          variant="contained"
          disabled={!allFieldsMapped}
          sx={{
            borderRadius: 2,
            px: 3,
            boxShadow: "0 4px 12px rgba(33,150,243,0.2)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(33,150,243,0.3)",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          {allFieldsMapped ? "Apply Mappings" : "All Fields Required"}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default MappingSuggestionModal;
